/**
 * People: who uses Numa in the selection, and how much.
 *
 * It opens with the people active in the time window. For one stack the
 * engagement map follows: every person on a ring by how many of the last 30
 * days they were active. Those 30 days run back from the day the data runs up
 * to, never from today's date, so a stack whose nightly update is behind is
 * not drawn as a stack nobody uses.
 *
 * Below it: active people by day, and one table of everyone.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import { Bar as BarChart } from 'react-chartjs-2';
import '../chartSetup';
import { ND_COLORS } from '@/styles/theme';
import type { ClientSnapshot, DashboardView } from '@/types/fleetAnalytics';
import { formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import { buildUserPlanets, userRingLabels } from '../clientUserMap';
import type { UserEngagementBucket, UserMapMetrics, UserRecency } from '../clientUserMap';
import { Constellation } from '../Constellation';
import { useCurrency } from '../currencyContext';
import {
  isReady,
  needsOf,
  selectActiveUsers,
  selectEngagement,
  selectPeakDailyUsers,
  selectPerActivePerson,
  selectProvisionedUsers,
  selectSeries,
  selectUserRows,
} from '../selectors';
import type { UserRow } from '../selectors';
import { dayWords, fmtN, fmtSpan, fmtUSD, fmtUSDc, isInfraAccount, stackLabel } from '../shared';
import type { WindowState } from '../shared';
import { Empty, Fact, FactGroup, Facts, Kpi, KpiGrid, NeedsTier, Panel } from '../ui/kit';
import type { Scope } from '../ui/kit';
import { dayLabel, LinkButton, Quiet, SearchBox, sortRows, SortTh, Swatch, tile, useSort } from './common';
import type { SortValue } from './common';
import type { DashboardTabProps } from './registry';
import './People.css';

const oneDecimal = (n: number): string => n.toLocaleString(undefined, { maximumFractionDigits: 1 });

// ─── the tab ─────────────────────────────────────────────────────────────

export function People({ data, window: win, fleet, selection, onSelectClient }: DashboardTabProps) {
  useCurrency();
  const provisioned = useMemo(() => selectProvisionedUsers(data), [data]);
  const active = useMemo(() => selectActiveUsers(data, win), [data, win]);
  const perPerson = useMemo(() => selectPerActivePerson(data, win), [data, win]);

  // The figures that count people need the per-person data. Ask for it; each
  // reads "…" until it is in, and the rest of the tab does not wait.
  const { ensureTier } = fleet;
  const waiting = needsOf(active).join(',');
  useEffect(() => {
    needsOf(active).forEach((tier) => ensureTier(tier));
  }, [ensureTier, waiting]);

  const stack = selection.kind === 'stack' ? selection.stacks[0] : undefined;
  const stacks = useMemo(() => selection.stacks.filter((s) => !isInfraAccount(s)), [selection.stacks]);

  return (
    <>
      <KpiGrid cols={4}>
        <Kpi
          lead
          label="Active people"
          {...tile(active, fmtN, 'People who sent a message in the window, each counted once.')}
          sub="Sent one message or more"
        />
        <Kpi label="Provisioned" {...tile(provisioned, fmtN)} sub="People with a login" />
        <Kpi label="Messages per active person" {...tile(perPerson.messages, oneDecimal)} />
        <Kpi label="Cost per active person" {...tile(perPerson.cost, fmtUSD)} sub="Model spend from chat and agents" />
      </KpiGrid>

      {stack && (
        <NeedsTier fleet={fleet} tiers={['users']} what="Per-person history">
          <EngagementMap stack={stack} asOf={selection.asOf} label={selection.label} />
        </NeedsTier>
      )}

      <ActivePerDay data={data} win={win} />

      <NeedsTier fleet={fleet} tiers={['users']} what="Per-person history">
        <Everyone stacks={stacks} win={win} isView={selection.kind === 'view'} onSelectClient={onSelectClient} />
      </NeedsTier>
    </>
  );
}

// ─── engagement: the map and its four bands ──────────────────────────────

const BANDS: Array<{ key: UserEngagementBucket; label: string; rule: string; days: string }> = [
  { key: 'power', label: 'Power', rule: 'Active on 15 days or more', days: '15 days or more' },
  { key: 'engaged', label: 'Engaged', rule: 'Active on 6 to 14 days', days: '6 to 14 days' },
  { key: 'casual', label: 'Casual', rule: 'Active on 1 to 5 days', days: '1 to 5 days' },
  { key: 'dormant', label: 'Dormant', rule: 'Not active on any day', days: 'no day' },
];

// The four colours the map draws a person in, from the same source the map reads. Each comes with its words.
const RECENCY: Array<{ key: UserRecency; label: string; colour: string }> = [
  { key: 'recent', label: 'In the last 7 days', colour: ND_COLORS.good },
  { key: 'lapsing', label: '8 to 14 days before', colour: ND_COLORS.warn },
  { key: 'going_dark', label: '15 to 30 days before', colour: ND_COLORS.bad },
  { key: 'dormant', label: 'Longer, or never', colour: ND_COLORS.neutral },
];

interface EngagementMapProps {
  stack: ClientSnapshot;
  asOf: string;
  label: string;
}

function EngagementMap({ stack, asOf, label }: EngagementMapProps) {
  const currency = useCurrency();
  const engagement = useMemo(() => selectEngagement(stack, asOf), [stack, asOf]);
  const [selected, setSelected] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  // Another stack has other people: start clean.
  useEffect(() => {
    setSelected(null);
    setQuery('');
  }, [stack.client]);

  const ready = isReady(engagement) ? engagement : null;
  // The hover card on a circle shows money, so the circles are built again when the currency changes.
  const planets = useMemo(
    () => (ready ? buildUserPlanets(ready.metrics, ready.stats) : []),
    [ready, currency.code, currency.rate]
  );
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!ready || !q) return null;
    return new Set(
      ready.metrics
        .filter((m) => m.displayName.toLowerCase().includes(q) || m.userId.toLowerCase().includes(q))
        .map((m) => m.userId)
    );
  }, [ready, query]);

  if (!ready) return <Empty>Loading per-person history</Empty>;

  const person = selected ? ready.metrics.find((m) => m.userId === selected) : undefined;
  const day = dayWords(ready.asOf);

  return (
    <div className="people-engagement">
      <Panel
        title="Who uses Numa, and how often"
        scope="last-30d"
        subtitle={`${fmtN(ready.people)} people: everyone with a login, and anyone with a message on record.`}
        actions={
          ready.people > 0 ? (
            <SearchBox value={query} onChange={setQuery} placeholder="Find a person" label="Find a person on the map" />
          ) : undefined
        }
      >
        {ready.people === 0 ? (
          <Empty>No person on record for this stack.</Empty>
        ) : (
          <>
            <Constellation
              planets={planets}
              ringLabels={userRingLabels()}
              centerLabel={label}
              selectedKey={selected}
              onSelect={setSelected}
              highlightKeys={matches}
            />
            {matches && (
              <p className="dash-note">
                {matches.size === 0
                  ? `Nobody on the map matches "${query.trim()}".`
                  : `${fmtN(matches.size)} of ${fmtN(ready.people)} people match. The rest are faded.`}
              </p>
            )}
          </>
        )}
      </Panel>

      <div className="people-engagement__side">
        <Panel title="People by days active" scope="last-30d" subtitle={`The 30 days to ${day}.`}>
          <Facts cols={1}>
            <FactGroup>
              {BANDS.map((b) => (
                <Fact
                  key={b.key}
                  label={`${b.label}: ${b.days}`}
                  {...tile(ready.bands[b.key], fmtN, ready.note)}
                  scope={undefined}
                />
              ))}
            </FactGroup>
          </Facts>
        </Panel>
        {person ? (
          <PersonCard person={person} stack={stack} day={day} onClear={() => setSelected(null)} />
        ) : (
          <MapKey day={day} />
        )}
      </div>
    </div>
  );
}

/** What the map's distance, size, colour and arrow mean. */
function MapKey({ day }: { day: string }) {
  return (
    <Panel title="How to read the map" subtitle={`Every figure covers the 30 days to ${day}.`}>
      <dl className="people-key">
        <dt>Ring</dt>
        <dd>The days a person was active. The centre is the most.</dd>
        <dt>Size</dt>
        <dd>The person's model spend.</dd>
        <dt>Colour</dt>
        <dd>
          When the person was last active:
          <ul className="people-key__colours">
            {RECENCY.map((r) => (
              <li key={r.key}>
                <Swatch colour={r.colour} />
                {r.label}
              </li>
            ))}
          </ul>
        </dd>
        <dt>Arrow</dt>
        <dd>Spend in the last 7 days is more than 20% above or below the 7 days before.</dd>
      </dl>
      <p className="dash-note">Select a circle to see one person.</p>
    </Panel>
  );
}

interface PersonCardProps {
  person: UserMapMetrics;
  stack: ClientSnapshot;
  day: string;
  onClear: () => void;
}

/** The person selected on the map. */
function PersonCard({ person, stack, day, onClear }: PersonCardProps) {
  const email = stack.users?.sub_to_email?.[person.userId];
  const band = BANDS.find((b) => b.key === person.engagementBucket);
  return (
    <Panel
      title="Selected person"
      scope="last-30d"
      subtitle={`The 30 days to ${day}.`}
      actions={
        <Button variant="outline-secondary" size="sm" onClick={onClear}>
          Clear
        </Button>
      }
    >
      <div className="people-person" title={email ?? person.userId}>
        {email ?? `No longer has a login (${person.userId.slice(0, 8)})`}
      </div>
      <dl className="people-facts">
        <dt>Band</dt>
        <dd>{band ? `${band.label}: ${band.rule.toLowerCase()}` : <Quiet />}</dd>
        <dt>Days active</dt>
        <dd className="numeric">{fmtN(person.activeDays30d)} of 30</dd>
        <dt>Last active</dt>
        <dd>{person.lastActiveDay ? dayWords(person.lastActiveDay) : 'Never'}</dd>
        <dt>Messages</dt>
        <dd className="numeric">{fmtN(person.messages30d)}</dd>
        <dt>Model spend</dt>
        <dd className="numeric">{fmtUSD(person.cost30d)}</dd>
        <dt>Last 7 days</dt>
        <dd className="numeric">
          {fmtUSD(person.cost7d)}
          <span className="people-facts__sub">{fmtUSD(person.costPrior7d)} in the 7 days before</span>
        </dd>
      </dl>
    </Panel>
  );
}

// ─── active people per day ───────────────────────────────────────────────

function ActivePerDay({ data, win }: { data: DashboardView; win: WindowState }) {
  const series = useMemo(() => selectSeries(data, win, 'peopleActive'), [data, win]);
  const peak = useMemo(() => selectPeakDailyUsers(data, win), [data, win]);
  const title = 'Active people per day';

  if (!isReady(series)) {
    return (
      <Panel title={title}>
        <Empty>Loading the daily record</Empty>
      </Panel>
    );
  }
  if (!series.available || !series.values.some((v) => v > 0)) {
    return (
      <Panel title={title}>
        <Empty>
          {series.available
            ? 'Nobody sent a message in this window.'
            : 'This data has no daily record of active people.'}
        </Empty>
      </Panel>
    );
  }

  const days = series.days;
  return (
    <Panel title={title} subtitle={`The busiest day had ${fmtN(peak.value)} people.`}>
      <div className="dash-chart dash-chart--short">
        <BarChart
          data={{
            labels: days.map(dayLabel),
            datasets: [
              {
                label: 'Active people',
                data: series.values,
                backgroundColor: ND_COLORS.accent,
                borderRadius: 4,
                maxBarThickness: 24,
              },
            ],
          }}
          options={{
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  title: (items) => (items.length ? dayWords(days[items[0].dataIndex]) : ''),
                  label: (item) => `${fmtN(item.parsed.y)} active people`,
                },
              },
            },
            scales: {
              x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12 } },
              y: { beginAtZero: true, ticks: { precision: 0, callback: (v) => fmtN(Number(v)) } },
            },
          }}
        />
      </div>
    </Panel>
  );
}

// ─── everyone ────────────────────────────────────────────────────────────

type PersonKey =
  | 'person'
  | 'client'
  | 'lastActive'
  | 'messages'
  | 'conversations'
  | 'spend'
  | 'costPerMessage'
  | 'avgLength'
  | 'toolCalls';

const PERSON_VALUE: Record<PersonKey, (r: UserRow) => SortValue> = {
  person: (r) => r.label.toLowerCase(),
  client: (r) => r.client,
  lastActive: (r) => r.lastAt,
  messages: (r) => r.messages,
  conversations: (r) => r.conversations,
  spend: (r) => r.cost,
  costPerMessage: (r) => r.costPerMessage,
  avgLength: (r) => r.avgSpanSeconds,
  toolCalls: (r) => r.toolCalls,
};

const PERSON_TEXT_COLUMNS: ReadonlySet<PersonKey> = new Set(['person', 'client']);

/** A view can hold some 1,600 people. The table draws this many, then more on request. */
const PAGE = 100;

interface EveryoneProps {
  stacks: ClientSnapshot[];
  win: WindowState;
  isView: boolean;
  onSelectClient: DashboardTabProps['onSelectClient'];
}

function Everyone({ stacks, win, isView, onSelectClient }: EveryoneProps) {
  useCurrency();
  const users = useMemo(() => selectUserRows(stacks, win), [stacks, win]);
  const { sort, onSort } = useSort<PersonKey>({ key: 'spend', dir: 'desc' }, PERSON_TEXT_COLUMNS);
  const [query, setQuery] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [limit, setLimit] = useState(PAGE);

  const clientLabel = useMemo(() => new Map(stacks.map((s) => [s.client, stackLabel(s)])), [stacks]);
  const all = isReady(users) ? users.rows : [];
  // "Active in this window" has a meaning only when messages follow the window.
  const canFilterActive = isReady(users) && users.scopes.messages === 'window';

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matching = all.filter((r) => {
      if (activeOnly && canFilterActive && !r.activeInWindow) return false;
      if (!q) return true;
      return (
        r.label.toLowerCase().includes(q) ||
        r.userId.toLowerCase().includes(q) ||
        (isView && (clientLabel.get(r.client) ?? r.client).toLowerCase().includes(q))
      );
    });
    return sortRows(matching, PERSON_VALUE[sort.key], sort.dir);
  }, [all, query, activeOnly, canFilterActive, sort, isView, clientLabel]);

  // A new search, filter or order starts from the top of the list.
  useEffect(() => setLimit(PAGE), [query, activeOnly, sort, stacks, win]);

  if (!isReady(users)) return <Empty>Loading per-person history</Empty>;

  const { scopes } = users;
  const shown = rows.slice(0, limit);
  const columns = isView ? 9 : 8;
  const tag = (scope: Scope): Scope | undefined => (scope === 'window' ? undefined : scope);

  return (
    <Panel
      title="Everyone"
      subtitle={
        <>
          {fmtN(all.length)} {all.length === 1 ? 'person' : 'people'}
          {isView ? ` in ${fmtN(stacks.length)} stacks` : ''}: everyone with a login, and anyone with a message on
          record.
          {users.neverActive > 0
            ? ` ${fmtN(users.neverActive)} ${users.neverActive === 1 ? 'has' : 'have'} a login and never sent a message.`
            : ''}
        </>
      }
      actions={
        all.length > 0 ? (
          <div className="dash-tools">
            {canFilterActive && (
              <Form.Check
                type="switch"
                id="people-active-only"
                label="Active in this window"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
            )}
            <SearchBox
              value={query}
              onChange={setQuery}
              placeholder={isView ? 'Search people and clients' : 'Search people'}
              label="Search the people table"
            />
          </div>
        ) : undefined
      }
      flush
    >
      {all.length === 0 ? (
        <Empty>Nobody has used Numa here yet.</Empty>
      ) : (
        <>
          <div className="dash-scroll">
            <Table className="dash-table">
              <thead>
                <tr>
                  <SortTh column="person" sort={sort} onSort={onSort}>
                    Person
                  </SortTh>
                  {isView && (
                    <SortTh column="client" sort={sort} onSort={onSort}>
                      Client
                    </SortTh>
                  )}
                  <SortTh column="lastActive" sort={sort} onSort={onSort}>
                    Last active
                  </SortTh>
                  <SortTh column="messages" sort={sort} onSort={onSort} numeric scope={tag(scopes.messages)}>
                    Messages
                  </SortTh>
                  <SortTh column="conversations" sort={sort} onSort={onSort} numeric scope={tag(scopes.conversations)}>
                    Conversations
                  </SortTh>
                  <SortTh column="spend" sort={sort} onSort={onSort} numeric scope={tag(scopes.cost)}>
                    Spend
                  </SortTh>
                  <SortTh
                    column="costPerMessage"
                    sort={sort}
                    onSort={onSort}
                    numeric
                    scope={scopes.cost === scopes.messages ? tag(scopes.cost) : undefined}
                  >
                    Cost per message
                  </SortTh>
                  <SortTh column="avgLength" sort={sort} onSort={onSort} numeric scope={tag(scopes.avgSpan)}>
                    Average conversation length
                  </SortTh>
                  <SortTh column="toolCalls" sort={sort} onSort={onSort} numeric scope={tag(scopes.toolCalls)}>
                    Tool calls
                  </SortTh>
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr>
                    <td colSpan={columns}>
                      <Empty>
                        {query.trim() ? `Nobody matches "${query.trim()}".` : 'Nobody sent a message in this window.'}
                      </Empty>
                    </td>
                  </tr>
                )}
                {shown.map((r) => (
                  <tr key={`${r.client}:${r.userId}`}>
                    <td>
                      <div className="dash-name dash-clip" title={r.email ?? r.userId}>
                        {r.label}
                      </div>
                    </td>
                    {isView && (
                      <td>
                        <LinkButton onClick={() => onSelectClient(r.client)}>
                          {clientLabel.get(r.client) ?? r.client}
                        </LinkButton>
                      </td>
                    )}
                    <td title={r.lastAt ? formatNzDateTime(r.lastAt) : undefined}>
                      {r.lastAt ? timeAgo(r.lastAt) : <Quiet />}
                    </td>
                    <td className="numeric text-end">{fmtN(r.messages)}</td>
                    <td className="numeric text-end">{fmtN(r.conversations)}</td>
                    <td className="numeric text-end">{fmtUSD(r.cost)}</td>
                    <td className="numeric text-end">
                      {r.costPerMessage === null ? <Quiet /> : fmtUSDc(r.costPerMessage)}
                    </td>
                    <td className="numeric text-end">
                      {r.avgSpanSeconds === null ? <Quiet /> : fmtSpan(r.avgSpanSeconds)}
                    </td>
                    <td className="numeric text-end">{fmtN(r.toolCalls)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          {users.note && <p className="dash-note dash-note--strip">{users.note}</p>}
          {rows.length > 0 && (
            <div className="people-more">
              <span>
                Showing {fmtN(shown.length)} of {fmtN(rows.length)}
              </span>
              {rows.length > shown.length && (
                <Button variant="outline-secondary" size="sm" onClick={() => setLimit((n) => n + PAGE)}>
                  Show {fmtN(Math.min(PAGE, rows.length - shown.length))} more
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </Panel>
  );
}
